/**
 * 표시 규칙 — **화면 두 벌의 잣대가 여기서 갈린다.**
 *
 * `rateText()` 운영자용 — n ≥ 10 이면 `NN%` + `k/n · 95% CI`, **n < 10 이면 퍼센트를 감추고 `k/n` 과
 *   "표본 부족"** 만(0 건이면 `—` 와 "표본 없음"). 감추는 것은 비율이지 표본이 아니다 — 운영자는 분모를
 *   봐야 수집 품질을 판단한다. LLM 응답은 비결정적이라 몇 샘플로 패턴을 그리지 않게 막는 것이 이
 *   함수의 일이다 (F-12 · AGENTS.md 검증 절).
 * `customerRate()` 고객용 — 표본에 관한 정보를 **전혀** 싣지 않는다 (2026-09-21 운영자 방침,
 *   260921-client-report-chart-only). F-12 의 표시 규율에서 고객 화면만 떼어낸 것이지 폐기가 아니다.
 */
import type { Rate } from '@geo-admin/shared/contract/common.ts';

export const MIN_SAMPLES = 10;

/**
 * ★ `minSamples` 는 표본 하한이다. 기본은 `MIN_SAMPLES`(F-12)이고, 운영자가 **분모가 작아도 비율을 보겠다**고
 *   정한 칸만 1 로 낮춘다(인용 일치율 — 분모가 「우리 사이트가 인용된 샘플」이라 대부분 10 에 못 미친다,
 *   운영자 결정 2026-10-12). 그때도 `k/n · 95% CI` 는 함께 보여 분모가 얇다는 것은 화면에 남는다
 */
export function rateText(
  r: Rate,
  minSamples = MIN_SAMPLES,
): { main: string; sub: string | null; insufficient: boolean } {
  if (r.n === 0) return { main: '—', sub: '표본 없음', insufficient: true };
  if (r.n < minSamples) return { main: `${r.k}/${r.n}`, sub: '표본 부족', insufficient: true };
  const pct = (x: number | null) => (x === null ? '?' : `${Math.round(x * 100)}`);
  return { main: `${pct(r.k / r.n)}%`, sub: `${r.k}/${r.n} · 95% CI ${pct(r.lo)}–${pct(r.hi)}%`, insufficient: false };
}

/**
 * 고객 화면의 비율 표기 — **표본에 관한 정보를 싣지 않는다**(2026-09-21 운영자 방침,
 * 260921-client-report-chart-only). 표본이 얇아도 비율을 그대로 보이고 `k/n`·`표본 부족`·`표본 없음`
 * 을 쓰지 않는다.
 * ★ 운영자 화면은 계속 `rateText()` 를 쓴다 — 거기서는 k/n·95% CI·표본 부족이 판단의 근거다.
 *   F-12 의 표시 규율에서 **고객 화면만** 떼어낸 지점이 여기다.
 */
export function customerRate(r: Rate): string {
  return r.n === 0 ? '—' : `${Math.round((r.k / r.n) * 100)}%`;
}

/**
 * 전기 대비 증감 — **표시값끼리** 비교한다(화면의 큰 숫자와 같은 잣대라야 고객이 두 기간을 직접
 * 견주었을 때 어긋나지 않는다).
 * ★ 어느 한쪽이라도 표본이 모자라면 **증감을 말하지 않는다** — 표본이 얇은 두 비율의 차이는 잡음이다(F-12).
 * ★ 방향은 화살표가 진다. 색으로만 말하지 않는다.
 */
export function deltaText(cur: Rate, prev: Rate): { mark: string; label: string } | null {
  if (cur.n < MIN_SAMPLES || prev.n < MIN_SAMPLES) return null;
  const d = Math.round((cur.k / cur.n) * 100) - Math.round((prev.k / prev.n) * 100);
  if (d === 0) return { mark: '', label: '변화 없음' };
  return { mark: d > 0 ? '▲' : '▼', label: `${Math.abs(d)}%p` };
}

const KST = 'Asia/Seoul';

/**
 * 온보딩 상태 한 줄 — **운영자 화면에서만** 채워진다(계약상 비-admin 은 null).
 * 고객 화면에는 온보딩을 표시하지 않는다.
 */
export function onboardingText(
  o: { enabled: boolean; day: number | null; days: number | null; factor: number | null } | null,
): string {
  if (!o?.enabled || o.day === null) return '';
  // 분모는 그 행에 박힌 기간이다 — 질문마다 다르다 (261001-onboarding-period-per-query)
  return ` · 온보딩 ${o.day}/${o.days ?? '?'}일차 ×${o.factor?.toFixed(3) ?? '?'}`;
}

/**
 * 리포트 기간 설명의 앞머리. 창의 끝은 **어제**다 — 진행 중인 오늘은 들어가지 않는다
 * (260921-report-snapshot-and-onboarding-throttle). 스냅샷이 아직 없으면 기간만 말한다.
 */
export function asOfText(asOf: string | null | undefined): string {
  if (!asOf) return '최근';
  const [, m, d] = asOf.split('-');
  return `${Number(m)}/${Number(d)} 기준`;
}

export function dateTime(iso: string | null): string {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('ko-KR', {
    timeZone: KST,
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(iso));
}

export function relative(iso: string | null): string {
  if (!iso) return '—';
  const sec = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (sec < 60) return `${sec}초 전`;
  if (sec < 3600) return `${Math.round(sec / 60)}분 전`;
  if (sec < 86400) return `${Math.round(sec / 3600)}시간 전`;
  return `${Math.round(sec / 86400)}일 전`;
}

export const OUTCOME_LABEL: Record<string, string> = {
  ok: '정상',
  no_search: '검색 없음',
  partial: '잘림',
  blocked: '차단',
  error: '오류',
  abandoned: '회수됨',
};
